// Escape checks (GENETICS.md §3). The formula is normalized to [0, 0.95] —
// the original produced probabilities above 1 (a Leghorn at a 49-inch fence
// scored ≈119). Every factor here maps into [0, 1] before multiplying.
//
// Catching is the same model read the other way round (REFOCUS §12), not a
// second one: the same temperament, the same fence factor, the same cap.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type { ChickenEntity } from '../core/types';
import type { BiologyConfig } from './config';
import { docilityOf, flightinessOf } from './genetics';

export interface PerimeterContext {
  /** Tallest fence around the run, in inches. 0 = no fence at all. */
  fenceHeightInches: number;
  /** The main gate left unlatched multiplies the odds by 2.5. */
  gateUnlatched: boolean;
  /** A bird shut in the coop cannot leave regardless of temperament. */
  cooped: boolean;
}

/**
 * P(escape) = flightiness × (1 − docility) × fenceFactor × doorMultiplier,
 * capped at 0.95.
 */
export function escapeProbability(
  chicken: ChickenEntity,
  perimeter: PerimeterContext,
  config: BiologyConfig,
): number {
  if (perimeter.cooped || chicken.currentState === 'ESCAPED') return 0;
  // Chicks stay in the brooder; they are not going anywhere.
  if (chicken.stage === 'chick') return 0;

  const { flightiness, docility } = temperamentOf(chicken);
  const doorMultiplier = perimeter.gateUnlatched ? config.escape.doorOpenMultiplier : 1;

  return clamp(
    flightiness * (1 - docility) * fenceFactorFor(perimeter, config) * doorMultiplier,
    0,
    config.escape.maxProbability,
  );
}

/**
 * P(catch) = docility × (1 − flightiness) × fenceFactor, under the same cap.
 *
 * The same factors as the escape formula, read the other way round: a bird
 * that is hard to keep in is hard to get back, and a perimeter that holds her
 * near the run is the same perimeter that makes her findable. The gate
 * multiplier is deliberately absent — an unlatched gate is why she is out, and
 * it does nothing to make her easier to hold.
 */
export function catchProbability(
  chicken: ChickenEntity,
  perimeter: PerimeterContext,
  config: BiologyConfig,
): number {
  if (chicken.currentState !== 'ESCAPED' || chicken.stage === 'chick') return 0;

  const { flightiness, docility } = temperamentOf(chicken);

  return clamp(
    docility * (1 - flightiness) * fenceFactorFor(perimeter, config),
    0,
    config.escape.maxProbability,
  );
}

export function rollCatch(
  chicken: ChickenEntity,
  perimeter: PerimeterContext,
  config: BiologyConfig,
  rng: Rng,
): boolean {
  return rng.chance(catchProbability(chicken, perimeter, config));
}

/** Puts a caught bird back in the run, where her behaviour takes over again. */
export function recoverEscapee(chicken: ChickenEntity): void {
  chicken.currentState = 'IDLE';
}

/** Both formulas read the temperament the same way, from the same two numbers. */
function temperamentOf(chicken: ChickenEntity): { flightiness: number; docility: number } {
  return {
    flightiness: flightinessOf(chicken.breed, chicken.genotype) / 100,
    docility: docilityOf(chicken.breed, chicken.genotype) / 100,
  };
}

function fenceFactorFor(perimeter: PerimeterContext, config: BiologyConfig): number {
  const e = config.escape;
  return e.fenceConstantInches / (perimeter.fenceHeightInches + e.fenceConstantInches);
}

/** Runs on the check interval, not every tick — 60 ticks per GENETICS.md §3. */
export function shouldCheckEscape(tickCount: number, config: BiologyConfig): boolean {
  return tickCount % config.escape.checkIntervalTicks === 0;
}

export function rollEscape(
  chicken: ChickenEntity,
  perimeter: PerimeterContext,
  config: BiologyConfig,
  rng: Rng,
): boolean {
  return rng.chance(escapeProbability(chicken, perimeter, config));
}

/** Tallest fence in the yard; the perimeter is only as good as its lowest bit. */
export function fenceHeightFor(segments: Array<{ heightInches: number }>): number {
  if (segments.length === 0) return 0;
  return Math.min(...segments.map((s) => s.heightInches));
}
